/**
 * PropFirm Engine — pure business logic for prop firm rules.
 * Works with DB-driven PropFirmTier objects.
 *
 * Hierarchy: PropFirmTemplate → PropFirmAccountType → PropFirmTier
 */

import { isTrailingDrawdownType, isEodDrawdownType } from "./tierRuleValidation";
import { isPropFirmFundedStatus } from "./accountStatus";
import {
  applyDrawdownLock,
  legacyLockOffset,
  resolveDrawdownLockRule,
  type DrawdownLockSettings,
} from "./drawdownLock";

/**
 * Décalage historique du gel funded (+100 $ chez Apex, 0 ailleurs). Conservé
 * pour les appelants existants ; la règle complète vit dans drawdownLock.ts.
 */
export function fundedTrailingLockOffset(templateSlug: string | null | undefined): number {
  return legacyLockOffset(templateSlug);
}

/** Ce qu'il faut au moteur pour figer le plancher (voir drawdownLock.ts). */
export interface DrawdownLockContext {
  /** Colonnes du type de compte VIVANT. Null = comportement historique. */
  settings?: DrawdownLockSettings | null;
  /** Au moins une sortie d'argent du compte (`hasWithdrawnFromAccount`). */
  hasWithdrawal?: boolean;
}

export interface TierSnapshot {
  id: string;
  label: string;
  minBalance: number;
  maxBalance: number;
  /**
   * Taille nominale du compte à l'achat (150000 pour un palier $150k).
   * minBalance/maxBalance ne sont que des bornes d'appariement.
   *
   * Optionnel : la colonne est nullable en base et des paliers sans taille
   * existent (cf. le filtre `accountSize: { not: null }` de
   * tradovateOperations). Les snapshots construits à la main (tests, fixtures)
   * ne la fournissent pas non plus.
   */
  accountSize?: number | null;
  /** Prix du palier (mensuel pour un abonnement). */
  price?: number | null;
  profitTargetPercent: number | null;
  profitTargetAmount: number | null;
  minTradingDays: number | null;
  maxDailyLossPercent: number | null;
  maxDailyLossAmount: number | null;
  maxTotalLossPercent: number | null;
  maxTotalLossAmount: number | null;
  hasTrailingDrawdown: boolean;
  /**
   * Nature du plancher déclarée sur le PALIER : "intraday" | "eod" | "static".
   *
   * Le pendant de `PhaseSnapshot.drawdownType`, et il est arrivé tard : la
   * branche tier de `computeBufferStatus` ne se fiait qu'au booléen ci-dessus.
   * Or la prod passe à 100 % par cette branche (aucun compte ne résout de
   * phase), si bien que le type EOD/intraday de 153 comptes actifs n'était lu
   * par personne.
   *
   * Optionnel, et c'est essentiel : `undefined` fait retomber sur le booléen
   * seul, donc sur le comportement historique. Aucune fixture ni aucun snapshot
   * déjà gelé n'a besoin d'être touché.
   */
  drawdownType?: string | null;
  trailingDrawdownPercent: number | null;
  trailingDrawdownAmount: number | null;
  payoutThresholdPercent: number | null;
  payoutThresholdAmount?: number | null;
  payoutCooldownDays: number | null;
  /** Consistance appliquée en CHALLENGE (et aux statuts sans phase connue). */
  consistencyRulePercent: number | null;
  /**
   * Consistance appliquée aux statuts FUNDED (pa / funded / live). Les firms
   * n'imposent souvent pas la même règle des deux côtés (Apex Legacy : aucune
   * en challenge, 30 % en funded). Optionnel : les snapshots figés avant cette
   * colonne ne la portent pas, et se lisent « aucune règle funded ».
   */
  fundedConsistencyRulePercent?: number | null;
  maxContracts: number | null;
  // Frais d'activation one-shot payés au passage en funded (Topstep $149...).
  // Optionnel comme payoutThresholdAmount ci-dessus : les snapshots construits
  // à la main (tests, fixtures) ne le fournissent pas, alors que les rows
  // Prisma le portent toujours.
  activationFee?: number | null;
  /** Commissions round-trip du palier. null/absent = repli sur la firm. */
  commissionPerContract?: number | null;
  symbolCommissions?: unknown;
  phases?: PhaseSnapshot[];
}

export interface PhaseSnapshot {
  id: string;
  name: string;
  phaseType: 'evaluation' | 'evaluation_2' | 'funded' | 'funded_variant' | 'live';
  sortOrder: number;
  profitTargetPercent: number | null;
  profitTargetAmount: number | null;
  drawdownType: string | null;
  maxDrawdownPercent: number | null;
  maxDrawdownAmount: number | null;
  maxDailyLossPercent: number | null;
  maxDailyLossAmount: number | null;
  minTradingDays: number | null;
  maxTradingDays: number | null;
  minDaysToPass: number | null;
  maxContracts: number | null;
  maxMicroContracts: number | null;
  consistencyRulePercent: number | null;
  consistencyRelaxesAfterPayout: boolean;
  newsTrading: boolean | null;
  overnightHolding: boolean | null;
  weekendHolding: boolean | null;
  maeRulePercent: number | null;
  maxRiskRewardRatio: number | null;
  payoutSplitPercent: number | null;
  payoutFrequency: string | null;
  payoutCooldownDays: number | null;
  maxPayoutAmount: number | null;
  firstPayoutDays: number | null;
  minWinningDaysForPayout: number | null;
  minWinningDayProfit: number | null;
  payoutThresholdAmount: number | null;
  payoutsToLiveFunded: number | null;
  payoutCap100Percent: number | null;
  environment: string | null;
  bufferRequirement: boolean;
  commissionPerRT: number | null;
  commissionMicroPerRT: number | null;
  commissionNote: string | null;
  notes: string | null;
}

export interface AccountTypeSnapshot {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  /** Voir pricingModel.ts. Optionnel : les fixtures construites à la main ne le portent pas. */
  pricingModel?: string | null;
  /** Gel du plancher (voir drawdownLock.ts). Optionnels : absents des fixtures. */
  drawdownLockOffset?: number | null;
  drawdownLockMode?: string | null;
  drawdownLockScope?: string | null;
  tiers: TierSnapshot[];
}

export interface PropFirmTemplateSnapshot {
  id: string;
  slug: string;
  name: string;
  color: string;
  textColor: string;
  isSystem: boolean;
  commissionPerContract?: number | null;
  accountTypes: AccountTypeSnapshot[];
}

/**
 * Returns all tiers across all account types of a template (for backwards-compat helpers).
 */
function allTiers(template: PropFirmTemplateSnapshot): TierSnapshot[] {
  return template.accountTypes.flatMap((at) => at.tiers);
}

/**
 * Resolves the active account type for an account.
 * Uses the override account type if provided, otherwise returns the first active one.
 */
export function resolveAccountType(
  template: PropFirmTemplateSnapshot,
  overrideAccountTypeId?: string | null,
): AccountTypeSnapshot | null {
  if (!template.accountTypes.length) return null;

  if (overrideAccountTypeId) {
    const found = template.accountTypes.find((at) => at.id === overrideAccountTypeId);
    if (found) return found;
  }

  // Default: first active account type by sortOrder
  const active = [...template.accountTypes]
    .filter((at) => at.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return active[0] ?? null;
}

/**
 * Resolves the active tier for an account within a given account type.
 * Uses the override tier if provided, otherwise auto-detects from initialBalance.
 */
export function resolveTier(
  accountType: AccountTypeSnapshot,
  initialBalance: number,
  overrideTierId?: string | null,
): TierSnapshot | null {
  if (!accountType.tiers.length) return null;

  if (overrideTierId) {
    const override = accountType.tiers.find((t) => t.id === overrideTierId);
    if (override) return override;
  }

  // Auto-detect: find tier where minBalance <= initialBalance <= maxBalance
  const sorted = [...accountType.tiers].sort((a, b) => a.minBalance - b.minBalance);
  let matched: TierSnapshot | null = null;
  for (const tier of sorted) {
    if (initialBalance >= tier.minBalance && initialBalance <= tier.maxBalance) {
      matched = tier;
      break;
    }
  }
  // Fallback: largest tier whose minBalance <= initialBalance
  if (!matched) {
    for (const tier of sorted) {
      if (initialBalance >= tier.minBalance) {
        matched = tier;
      }
    }
  }
  return matched ?? sorted[0];
}

/**
 * Convenience: resolves both account type and tier from a template.
 *
 * When a tier override is provided, we first locate which account type owns that
 * tier (it may differ from the default account type), then use that account type.
 * This handles the case where propFirmAccountTypeId is null but the user has
 * explicitly selected a tier from a non-default account type (e.g. LucidFlex
 * when the default would be LucidPro).
 */
export function resolveAccountTypeAndTier(
  template: PropFirmTemplateSnapshot,
  initialBalance: number,
  overrideAccountTypeId?: string | null,
  overrideTierId?: string | null,
): { accountType: AccountTypeSnapshot | null; tier: TierSnapshot | null } {
  // If a tier override is given, find which account type owns it across all account types
  if (overrideTierId) {
    for (const at of template.accountTypes) {
      const tier = at.tiers.find((t) => t.id === overrideTierId);
      if (tier) return { accountType: at, tier };
    }
  }

  const accountType = resolveAccountType(template, overrideAccountTypeId);
  if (!accountType) return { accountType: null, tier: null };
  const tier = resolveTier(accountType, initialBalance, overrideTierId);
  return { accountType, tier };
}

export interface ChallengeProgress {
  profit: number;
  profitPercent: number;
  targetPercent: number | null;
  targetAmount: number | null; // absolute $ target (takes priority over percent if set)
  ratio: number;
  daysTraded: number;
  minTradingDays: number | null;
  daysRatio: number;
  isComplete: boolean;
}

export function computeChallengeProgress(
  initialBalance: number,
  currentBalance: number,
  tradingDates: string[],
  tier: TierSnapshot | null,
  accountStatus?: string | null,
): ChallengeProgress {
  // BASE DE L'OBJECTIF : la taille du compte À L'ACHAT, pas `initialBalance`.
  //
  // Sur un compte synchronisé, `initialBalance` est dérivé du solde broker à la
  // PREMIÈRE connexion (deriveInitialBalance). Des trades antérieurs à cette
  // connexion sont donc déjà absorbés dedans : un compte 150k acheté à 150 000 $
  // se retrouve enregistré à 151 104 $, et l'objectif se calcule contre une base
  // gonflée du PnL pré-sync — il affichait 4 807 / 9 000 là où le trader lit
  // 4 890 sur son relevé.
  //
  // `accountSize` est la valeur contractuelle du palier, insensible à la date de
  // connexion : c'est la seule base qui redonne le même chiffre que la prop firm.
  //
  // FALLBACK sur `initialBalance` quand le palier n'a pas de taille renseignée
  // (colonne nullable) : comportement historique conservé, aucune régression
  // pour les paliers incomplets.
  const objectiveBase = tier?.accountSize ?? initialBalance;

  const profit = currentBalance - objectiveBase;
  const profitPercent = objectiveBase > 0 ? (profit / objectiveBase) * 100 : 0;

  const activePhase = tier && accountStatus ? resolvePhase(tier, accountStatus) : null;

  const targetAmount: number | null =
    activePhase?.profitTargetAmount ??
    tier?.profitTargetAmount ??
    null;
  const targetPercent: number | null =
    activePhase?.profitTargetPercent ??
    tier?.profitTargetPercent ??
    null;

  // Sans cible configuree, l'objectif est INCONNU, pas atteint. Le defaut
  // inverse (profitMet = true) faisait declarer « Objectif atteint » n'importe
  // quel compte sans palier renseigne, y compris un challenge crame a -1500 $,
  // et suffisait a lui seul a declencher detectTargetReached (passage funded).
  let ratio = 0;
  let profitMet = false;
  if (targetAmount !== null) {
    ratio = Math.min(1, Math.max(0, profit / targetAmount));
    profitMet = profit >= targetAmount;
  } else if (targetPercent !== null) {
    ratio = Math.min(1, Math.max(0, profitPercent / targetPercent));
    profitMet = profitPercent >= targetPercent;
  }

  const minDays =
    activePhase?.minTradingDays ??
    tier?.minTradingDays ??
    null;
  const uniqueDays = new Set(tradingDates).size;
  const daysRatio = minDays ? Math.min(1, uniqueDays / minDays) : 1;
  const daysMet = minDays !== null ? uniqueDays >= minDays : true;

  return {
    profit,
    profitPercent,
    targetPercent,
    targetAmount,
    ratio,
    daysTraded: uniqueDays,
    minTradingDays: minDays,
    daysRatio,
    isComplete: profitMet && daysMet,
  };
}

export interface BufferStatus {
  ruleType: "daily_loss" | "total_loss" | "trailing_drawdown" | null;
  maxAllowedLoss: number;
  consumedLoss: number;
  remainingRoom: number;
  usedRatio: number;
  isWarning: boolean;
  isViolation: boolean;
  highWaterMark?: number;
  floorAmount?: number;  // absolute floor value ($)
}

export function computeBufferStatus(
  initialBalance: number,
  currentBalance: number,
  tier: TierSnapshot | null,
  peakBalance?: number | null,
  dailyPnl?: number | null,
  accountStatus?: string | null,
  /** Slug du PropFirmTemplate : repli du gel quand le type de compte ne le règle pas (voir drawdownLock.ts). */
  templateSlug?: string | null,
  /**
   * Pic des soldes de CLOTURE (`Account.peakBalanceEod`), pour le trailing EOD.
   *
   * En DERNIERE position et optionnel : les dizaines d'appels existants
   * compilent et se comportent a l'identique. `null`/omis = aucune cloture
   * observee -> repli sur `peakBalance`, donc comportement historique. Voir
   * eodPeakBalance.ts.
   */
  peakBalanceEod?: number | null,
  /**
   * Gel du plancher réglé sur le type de compte. Omis = comportement
   * historique (+100 Apex, gel au trailing, funded seulement).
   */
  drawdownLock?: DrawdownLockContext | null,
): BufferStatus {
  const noRule: BufferStatus = {
    ruleType: null,
    maxAllowedLoss: 0,
    consumedLoss: 0,
    remainingRoom: Infinity,
    usedRatio: 0,
    isWarning: false,
    isViolation: false,
  };

  if (!tier) return noRule;

  // Resolve active phase from accountStatus if provided
  const activePhase = accountStatus ? resolvePhase(tier, accountStatus) : null;

  // Extract drawdown rules from phase, fall back to tier flat fields
  let hasTrailingDrawdown: boolean;
  let trailingDrawdownAmount: number | null;
  let trailingDrawdownPercent: number | null;
  let maxTotalLossAmount: number | null;
  let maxTotalLossPercent: number | null;
  let maxDailyLossAmount: number | null;
  let maxDailyLossPercent: number | null;
  // Nature EFFECTIVE du plancher, apres resolution phase/tier. Elle decide
  // lequel des deux pics ancre le trailing (voir plus bas).
  let effectiveDrawdownType: string | null;

  if (activePhase) {
    // ⚠️ NE PAS revenir à `=== "trailing"`. Cette comparaison littérale a
    // existé ici et était INERTE : le référentiel n'écrit JAMAIS "trailing", il
    // écrit 'eod' (110 phases) ou 'intraday' (31). La comparaison rendait donc
    // false, le montant partait en perte totale STATIQUE (lignes suivantes), et
    // le plancher se figeait au solde d'ouverture au lieu de suivre le pic.
    // Résultat : breach jamais détecté, puis clôture archivée « challenge
    // validé ». Un test échoue désormais sur chaque palier du référentiel
    // (referentialEngineCoverage.test.ts).
    //
    // Le vocabulaire a UNE seule source de vérité, partagée avec la validation
    // de saisie : `isTrailingDrawdownType`. Les deux avaient divergé, et c'est
    // précisément cette divergence qui a produit le défaut.
    hasTrailingDrawdown = isTrailingDrawdownType(activePhase.drawdownType);
    trailingDrawdownAmount = hasTrailingDrawdown ? (activePhase.maxDrawdownAmount ?? null) : null;
    trailingDrawdownPercent = hasTrailingDrawdown ? (activePhase.maxDrawdownPercent ?? null) : null;
    maxTotalLossAmount = !hasTrailingDrawdown ? (activePhase.maxDrawdownAmount ?? null) : null;
    maxTotalLossPercent = !hasTrailingDrawdown ? (activePhase.maxDrawdownPercent ?? null) : null;
    maxDailyLossAmount = activePhase.maxDailyLossAmount ?? null;
    maxDailyLossPercent = activePhase.maxDailyLossPercent ?? null;
    effectiveDrawdownType = activePhase.drawdownType ?? null;
  } else {
    // LE TYPE DU PALIER EST LU, AU MÊME TITRE QUE CELUI DE LA PHASE.
    //
    // Cette branche ne regardait que le booléen, alors que la prod l'emprunte à
    // 100 % (aucun compte ne résout de phase : 6 paliers sur 76 en ont, et aucun
    // ne porte de compte). Le type EOD/intraday de 153 comptes actifs n'était
    // donc lu par personne, et le correctif de vocabulaire de la branche phase
    // ne les atteignait pas.
    //
    // `||` et non un remplacement : le booléen reste souverain quand il est
    // posé. Un palier historique sans `drawdownType` garde EXACTEMENT son
    // comportement, et un type inconnu ne peut ni activer ni désactiver le
    // trailing — il faut que ce changement soit invisible sur le parc existant.
    hasTrailingDrawdown =
      tier.hasTrailingDrawdown || isTrailingDrawdownType(tier.drawdownType);
    trailingDrawdownAmount = tier.trailingDrawdownAmount ?? null;
    trailingDrawdownPercent = tier.trailingDrawdownPercent ?? null;
    maxTotalLossAmount = tier.maxTotalLossAmount ?? null;
    maxTotalLossPercent = tier.maxTotalLossPercent ?? null;
    maxDailyLossAmount = tier.maxDailyLossAmount ?? null;
    maxDailyLossPercent = tier.maxDailyLossPercent ?? null;
    effectiveDrawdownType = tier.drawdownType ?? null;
  }

  const trailingDrawdownResolved = hasTrailingDrawdown
    ? trailingDrawdownAmount !== null
      ? trailingDrawdownAmount
      : trailingDrawdownPercent !== null
        ? (trailingDrawdownPercent / 100) * initialBalance
        : null
    : null;

  // PIC DE RÉFÉRENCE du trailing drawdown.
  //
  // `peakBalance` est le plus haut solde OBSERVÉ : c'est lui, et lui seul, qui
  // ancre le plancher d'un trailing. Sans valeur stockée on retombe sur
  // `max(initialBalance, currentBalance)`, qui ne mémorise rien — un plancher
  // qui suit le solde ne se déclenche jamais (cf. lucidTrailingFloor.test.ts).
  //
  // ⚠️ NE PAS re-plafonner ce pic au solde courant. Le cap d'origine visait les
  // PAYOUTS (un retrait fait descendre le solde sous le pic sans que le trader
  // ait rien perdu), mais il s'appliquait à TOUS les comptes : un compte
  // redescendu de son pic voyait son plancher redescendre avec lui, ce qui rend
  // `isViolation` mathématiquement impossible — soit exactement le contraire de
  // ce qu'un trailing doit faire. Le cas des payouts est déjà traité juste en
  // dessous, par la branche `hasReceivedPayout`.
  // ── QUEL PIC ANCRE LE PLANCHER ? EOD et intraday n'ont pas la même réponse ──
  //
  // Les deux trailings ne diffèrent que par le MOMENT où le pic monte :
  //   intraday -> dès qu'un trade fermé fait monter le solde  -> `peakBalance`
  //   EOD      -> uniquement au bilan de clôture de session   -> `peakBalanceEod`
  //
  // Le cas qui les sépare : une pointe à 52 000 en séance, une clôture à 50 500.
  // L'intraday retient 52 000 (le gain était réalisé), l'EOD retient 50 500 — et
  // 1 500 d'écart sur le plancher, c'est la différence entre un compte vivant et
  // un compte archivé « cramé ».
  //
  // REPLI OBLIGATOIRE sur `peakBalance` quand aucune clôture n'a encore été
  // observée. Ce n'est pas une précaution théorique : 42 comptes prod ont un
  // solde de plus de 24 h, donc aucune lecture ne tombera dans leur fenêtre de
  // clôture avant longtemps. Sans ce repli, ils perdraient toute surveillance de
  // trailing — l'exigence est de ne RIEN dégrader.
  const usesEodPeak =
    isEodDrawdownType(effectiveDrawdownType) &&
    peakBalanceEod !== null &&
    peakBalanceEod !== undefined &&
    Number.isFinite(peakBalanceEod);

  const rawPeak = usesEodPeak
    ? (peakBalanceEod as number)
    : peakBalance ?? Math.max(initialBalance, currentBalance);
  const peak = Math.max(rawPeak, initialBalance);

  // VIOLATION JOURNALIÈRE, calculée AVANT la cascade de `return` ci-dessous.
  //
  // Cette fonction répond à « quelle est ma marge ? » par UNE règle : la
  // première résolue gagne (trailing, puis total_loss, puis daily_loss) et les
  // suivantes ne sont jamais évaluées. Or plusieurs règles peuvent tuer un
  // compte simultanément — chez LucidPro, trailing (2 000) ET perte journalière
  // (1 200) coexistent, et seul le trailing était pris en compte.
  //
  // Ce n'est pas qu'un défaut d'affichage : le pipeline de sync DÉSACTIVE un
  // compte sur `isViolation` et s'en sert comme contre-preuve à la clôture. Un
  // compte qui cramait sa perte journalière n'était donc pas désactivé, et sa
  // clôture repartait en « challenge validé ».
  //
  // On sépare donc les deux questions : la jauge affichée reste celle de la
  // règle structurante (pas d'oscillation d'un jour à l'autre), mais
  // `isViolation` devient vrai dès qu'UNE règle est enfreinte.
  //
  // `dailyPnl` absent (le pipeline passe null) => aucune violation journalière
  // n'est inventée : une absence d'information n'est pas une preuve.
  const dailyLossLimit =
    maxDailyLossAmount !== null
      ? maxDailyLossAmount
      : maxDailyLossPercent !== null
        ? (maxDailyLossPercent / 100) * initialBalance
        : null;
  const dailyLossBreached =
    dailyLossLimit !== null && dailyPnl !== null && dailyPnl !== undefined
      ? Math.max(0, -dailyPnl) >= dailyLossLimit
      : false;

  if (trailingDrawdownResolved !== null) {
    // Le trailing se FIGE à `initial + offset` selon la règle du type de
    // compte (drawdownLock.ts) : par défaut en funded, quand le pic est assez
    // haut (+100 $ chez Apex). Certaines firms le figent aussi dès le premier
    // payout (LucidFlex). L'ancienne règle « plancher à 0 $ après un payout »
    // (Topstep) s'appliquait à toutes les prop firms : la marge d'un PA Apex
    // valait alors tout le solde.
    const maxDrawdown = trailingDrawdownResolved;
    const trailingFloor = peak - maxDrawdown;
    const drawdownFloor = applyDrawdownLock({
      trailingFloor,
      initialBalance,
      rule: resolveDrawdownLockRule(drawdownLock?.settings, templateSlug),
      funded: isPropFirmFundedStatus(accountStatus, true),
      hasWithdrawal: drawdownLock?.hasWithdrawal === true,
    });
    const remaining = Math.max(0, currentBalance - drawdownFloor);
    // Rapporté au drawdown du palier, pas au pic : sur un plancher figé, le pic
    // inclut les gains déjà retirés en payout, qui ne sont pas une perte.
    const consumed = Math.max(0, maxDrawdown - remaining);
    const usedRatio = maxDrawdown > 0 ? Math.min(1, consumed / maxDrawdown) : 0;
    return {
      ruleType: "trailing_drawdown",
      maxAllowedLoss: maxDrawdown,
      consumedLoss: consumed,
      remainingRoom: remaining,
      usedRatio,
      isWarning: !!(usedRatio >= 0.7 && currentBalance > drawdownFloor),
      isViolation: currentBalance <= drawdownFloor || dailyLossBreached,
      highWaterMark: peak,
      floorAmount: drawdownFloor,
    };
  }

  const maxTotalLossResolved =
    maxTotalLossAmount !== null
      ? maxTotalLossAmount
      : maxTotalLossPercent !== null
        ? (maxTotalLossPercent / 100) * initialBalance
        : null;

  if (maxTotalLossResolved !== null) {
    const maxLoss = maxTotalLossResolved;
    const consumed = Math.max(0, initialBalance - currentBalance);
    const remaining = Math.max(0, maxLoss - consumed);
    const usedRatio = maxLoss > 0 ? Math.min(1, consumed / maxLoss) : 0;
    return {
      ruleType: "total_loss",
      maxAllowedLoss: maxLoss,
      consumedLoss: consumed,
      remainingRoom: remaining,
      usedRatio,
      isWarning: usedRatio >= 0.7 && consumed < maxLoss,
      isViolation: consumed >= maxLoss || dailyLossBreached,
    };
  }

  const maxDailyLossResolved =
    maxDailyLossAmount !== null
      ? maxDailyLossAmount
      : maxDailyLossPercent !== null
        ? (maxDailyLossPercent / 100) * initialBalance
        : null;

  if (maxDailyLossResolved !== null && dailyPnl !== null && dailyPnl !== undefined) {
    const maxDailyLoss = maxDailyLossResolved;
    const dailyLoss = Math.max(0, -dailyPnl);
    const remaining = Math.max(0, maxDailyLoss - dailyLoss);
    const usedRatio = maxDailyLoss > 0 ? Math.min(1, dailyLoss / maxDailyLoss) : 0;
    return {
      ruleType: "daily_loss",
      maxAllowedLoss: maxDailyLoss,
      consumedLoss: dailyLoss,
      remainingRoom: remaining,
      usedRatio,
      isWarning: usedRatio >= 0.7 && dailyLoss < maxDailyLoss,
      isViolation: dailyLoss >= maxDailyLoss,
    };
  }

  return noRule;
}

// ─── Rule Violations ──────────────────────────────────────────────────────────

export type ViolationType =
  | "daily_loss_violation"
  | "daily_loss_warning"
  | "consistency_violation"
  | "consistency_warning";

export interface DayViolation {
  date: string; // "yyyy-MM-dd"
  types: ViolationType[];
  dailyPnl: number;
  maxDailyLossAllowed: number | null;
  consistencyRulePercent: number | null;
  // CHIFFRES DE LA CONSISTANCE, pour que l'UI puisse expliquer son verdict.
  //
  // Le tooltip disait « Règle de consistance violée » sans dire ni le
  // pourcentage atteint ni contre quoi il était comparé : un franchissement
  // réel (34 % contre 30 %) était indiscernable d'un bug d'affichage.
  //
  // OPTIONNELS à dessein : un client à jour peut lire un payload produit par
  // un serveur antérieur à ce correctif, et doit alors afficher le libellé
  // sans chiffre plutôt qu'un « NaN% ».
  /** Base de comparaison : profit réalisé en funded, sinon profit target du palier. */
  consistencyBase?: number | null;
  /** Part du jour dans cette base, en %. */
  daySharePercent?: number | null;
  totalProfit: number | null;
}

const FUNDED_STATUSES = new Set(["pa", "funded", "live"]);

/**
 * Règle de consistance effective d'un compte. SOURCE UNIQUE : le moteur
 * d'alertes et les écrans l'appellent tous, sinon l'écran affiche une règle et
 * l'alerte en applique une autre (c'était le cas : les écrans faisaient
 * `phase ?? palier`, le moteur non).
 *
 * Priorité : override du compte > phase active > palier selon le statut.
 * Une phase active fait autorité même sans consistance, pour qu'une phase
 * funded sans règle la désactive bien. Les statuts sans phase connue
 * (inactive, demo) gardent la règle challenge, comportement historique.
 */
export function resolveConsistencyRule(
  tier: Pick<TierSnapshot, "consistencyRulePercent" | "fundedConsistencyRulePercent"> | null,
  phase: Pick<PhaseSnapshot, "consistencyRulePercent"> | null,
  accountStatus: string | null | undefined,
  accountOverride: number | null | undefined,
): number | null {
  if (accountOverride != null) return accountOverride;
  if (phase) return phase.consistencyRulePercent ?? null;
  if (!tier) return null;
  return accountStatus && FUNDED_STATUSES.has(accountStatus)
    ? tier.fundedConsistencyRulePercent ?? null
    : tier.consistencyRulePercent ?? null;
}

/**
 * Computes rule violations for each trading day.
 * Returns a map from date string ("yyyy-MM-dd") to DayViolation.
 *
 * Account-level overrides (consistencyRulePercent, maxDailyLossPercent) take
 * precedence over tier values, allowing rules without a prop firm template.
 *
 * @param tradesByDate  Map of date → net PnL for that day
 * @param totalProfit   Total profit of the account (currentBalance - initialBalance)
 * @param initialBalance  Account initial balance
 * @param tier  Active tier with rule parameters (may be null)
 * @param accountOverrides  Per-account rule overrides (no template needed)
 */
export function computeRuleViolations(
  tradesByDate: Map<string, number>,
  totalProfit: number,
  initialBalance: number,
  tier: TierSnapshot | null,
  accountOverrides?: { consistencyRulePercent?: number | null; maxDailyLossPercent?: number | null },
  accountStatus?: string | null,
): Map<string, DayViolation> {
  const result = new Map<string, DayViolation>();

  const activePhase = tier && accountStatus ? resolvePhase(tier, accountStatus) : null;

  const consistencyRulePercent = resolveConsistencyRule(
    tier,
    activePhase,
    accountStatus,
    accountOverrides?.consistencyRulePercent,
  );
  const maxDailyLossPercent =
    accountOverrides?.maxDailyLossPercent ??
    (activePhase ? activePhase.maxDailyLossPercent : tier?.maxDailyLossPercent ?? null);

  // PERTE JOURNALIÈRE : montant ET pourcentage.
  //
  // Seul le pourcentage était lu ici, alors que la moitié des paliers réels
  // renseignent un MONTANT (Lucid : 1 000 / 1 200 / 1 800 / 2 700). Leur règle
  // n'était donc évaluée nulle part : `computeBufferStatus` a bien une branche
  // daily_loss, mais elle est précédée des branches trailing et total_loss qui
  // font `return` — chez un palier à trailing, elle n'est jamais atteinte.
  //
  // Le pourcentage reste PRIORITAIRE quand les deux sont posés, comme partout
  // ailleurs dans le moteur (cf. maxTotalLoss, trailingDrawdown).
  //
  // SÉMANTIQUE : plancher FIXE ancré sur le solde d'ouverture du jour, remis à
  // zéro chaque jour. C'est donc le PnL NET de la journée qui est comparé, pas
  // la chute depuis le plus haut intraday : monter à +300 autorise bien une
  // perte de 1 500 avant de toucher un plancher à −1 200.
  const maxDailyLossAmount =
    activePhase ? activePhase.maxDailyLossAmount : tier?.maxDailyLossAmount ?? null;

  const maxDailyLossAllowed =
    maxDailyLossPercent !== null
      ? (maxDailyLossPercent / 100) * initialBalance
      : maxDailyLossAmount !== null
        ? maxDailyLossAmount
        : null;

  if (consistencyRulePercent === null && maxDailyLossAllowed === null) return result;

  // BASE DE LA CONSISTANCE : profit réalisé en funded, objectif en challenge.
  //
  // En funded, la part du jour se mesure contre le PROFIT RÉALISÉ courant du
  // compte — c'est le calcul du dashboard Apex (meilleure journée / PnL total,
  // réévalué à chaque nouveau profit : une journée « à 33 % » redevient
  // conforme quand le profit grandit). Un compte funded n'a pas d'objectif de
  // profit ; la chaîne `?? tier.profitTargetAmount` faisait fuir l'objectif du
  // CHALLENGE (3 000 $ sur un 50k) dans la base d'un PA : trois journées à
  // ~1 000 $ ressortaient « violées » à 33,7 % pendant qu'Apex affichait
  // 28,05 % / 30 % — règle respectée.
  //
  // En challenge, l'objectif reste la base : mesurer contre le profit réalisé
  // ferait hurler la règle dès la première journée gagnante (part = 100 %).
  const isFundedStatus = accountStatus != null && FUNDED_STATUSES.has(accountStatus);
  const profitTarget = isFundedStatus
    ? null
    : activePhase?.profitTargetAmount ??
      (activePhase?.profitTargetPercent != null
        ? (activePhase.profitTargetPercent / 100) * initialBalance
        : null) ??
      tier?.profitTargetAmount ??
      (tier?.profitTargetPercent != null
        ? (tier.profitTargetPercent / 100) * initialBalance
        : null);
  const consistencyBase = profitTarget ?? Math.max(totalProfit, 0);

  for (const [date, dailyPnl] of tradesByDate) {
    const types: ViolationType[] = [];
    // Remonté hors du bloc consistance pour être joint au verdict : c'est le
    // chiffre que l'utilisateur doit pouvoir lire pour vérifier la règle.
    let daySharePercent: number | null = null;

    if (maxDailyLossAllowed !== null && dailyPnl < 0) {
      const dailyLoss = Math.abs(dailyPnl);
      const usedRatio = dailyLoss / maxDailyLossAllowed;
      if (usedRatio >= 1) {
        types.push("daily_loss_violation");
      } else if (usedRatio >= 0.7) {
        types.push("daily_loss_warning");
      }
    }

    if (
      consistencyRulePercent !== null &&
      dailyPnl > 0 &&
      consistencyBase > 0
    ) {
      daySharePercent = (dailyPnl / consistencyBase) * 100;
      if (daySharePercent >= consistencyRulePercent) {
        types.push("consistency_violation");
      } else if (daySharePercent >= consistencyRulePercent * 0.7) {
        types.push("consistency_warning");
      }
    }

    if (types.length > 0) {
      result.set(date, {
        date,
        types,
        dailyPnl,
        maxDailyLossAllowed,
        consistencyRulePercent,
        consistencyBase: consistencyRulePercent !== null ? consistencyBase : null,
        daySharePercent,
        totalProfit,
      });
    }
  }

  return result;
}

export interface PayoutProgress {
  targetAmount: number | null;
  currentProfit: number;
  ratio: number;
  isReached: boolean;
  cooldownDays: number | null;
}

export function computePayoutProgress(
  initialBalance: number,
  currentBalance: number,
  tier: TierSnapshot | null,
  customPayoutTarget?: number | null,
  accountStatus?: string | null,
): PayoutProgress {
  const profit = Math.max(0, currentBalance - initialBalance);

  const activePhase = tier && accountStatus ? resolvePhase(tier, accountStatus) : null;

  let targetAmount: number | null = null;
  if (customPayoutTarget && customPayoutTarget > 0) {
    targetAmount = customPayoutTarget;
  } else if (activePhase?.payoutThresholdAmount != null) {
    targetAmount = activePhase.payoutThresholdAmount;
  } else if (tier?.payoutThresholdPercent != null) {
    targetAmount = (tier.payoutThresholdPercent / 100) * initialBalance;
  } else if (tier?.payoutThresholdAmount != null) {
    targetAmount = tier.payoutThresholdAmount;
  }

  const cooldownDays =
    activePhase?.payoutCooldownDays ??
    tier?.payoutCooldownDays ??
    null;

  const ratio = targetAmount ? Math.min(1, profit / targetAmount) : 0;

  return {
    targetAmount,
    currentProfit: profit,
    ratio,
    isReached: targetAmount !== null ? profit >= targetAmount : false,
    cooldownDays,
  };
}

/**
 * Resolves the active phase for a given account status.
 * Returns null if the tier has no phases (backward-compat fallback to tier flat fields).
 * "challenge"   → first evaluation phase
 * "challenge_2" → first evaluation_2 phase (null if none — falls back to tier flat)
 * "pa" / "live" → first funded phase (prefer "funded" over "funded_variant")
 * "live"        → first live phase, then funded, then funded_variant
 */
export function resolvePhase(
  tier: TierSnapshot,
  accountStatus: string,
): PhaseSnapshot | null {
  if (!tier.phases?.length) return null;

  const sorted = [...tier.phases].sort((a, b) => a.sortOrder - b.sortOrder);

  if (accountStatus === "challenge") {
    return sorted.find((p) => p.phaseType === "evaluation") ?? null;
  }

  if (accountStatus === "challenge_2") {
    return sorted.find((p) => p.phaseType === "evaluation_2") ?? null;
  }

  if (accountStatus === "live") {
    return (
      sorted.find((p) => p.phaseType === "live") ??
      sorted.find((p) => p.phaseType === "funded") ??
      sorted.find((p) => p.phaseType === "funded_variant") ??
      null
    );
  }

  // pa / funded
  return (
    sorted.find((p) => p.phaseType === "funded") ??
    sorted.find((p) => p.phaseType === "funded_variant") ??
    null
  );
}

/**
 * Returns all funded variant phases for a tier (Tradeify Select: Daily + Flex).
 * Returns empty array if no variants (regular propfirms).
 */
export function getFundedVariants(tier: TierSnapshot): PhaseSnapshot[] {
  if (!tier.phases?.length) return [];
  return [...tier.phases]
    .filter((p) => p.phaseType === "funded_variant")
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
